import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Download, Plus, ExternalLink, Trash2, Infinity as InfinityIcon, GitCompare } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import api from '@/lib/api';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useTheme } from '@/contexts/ThemeContext';

// The usage report is also the PDF artwork, so its colours are inline rather than
// token-driven. Two palettes: the dark one for reading on screen, the light one
// forced during export so the PDF stays print-safe.
const REPORT_PALETTE = {
  light: {
    bg: '#FFFFFF', ink: '#111827', ink2: '#374151', muted: '#6B7280', faint: '#9CA3AF',
    line: '#E5E7EB', lineSoft: '#F3F4F6', panel: '#F9FAFB',
    bar: '#111827', chartBar: '#818cf8', tagBg: '#FEF2F2', tagFg: '#B91C1C',
  },
  dark: {
    bg: '#26282C', ink: '#F2F3F5', ink2: '#D3D5DA', muted: '#9B9EA5', faint: '#7A7D84',
    line: '#383A3F', lineSoft: '#333539', panel: '#303236',
    bar: '#F3CF5B', chartBar: '#F3CF5B', tagBg: '#3A1A18', tagFg: '#FF8F86',
  },
};

export default function ReportsPage() {
  const { t } = useTranslation();
  const { user, hasPerm } = useAuth();
  const [report, setReport] = useState([]);
  const [sponsorReports, setSponsorReports] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [printMode, setPrintMode] = useState(false);
  const { theme } = useTheme();
  const P = REPORT_PALETTE[theme === 'dark' && !printMode ? 'dark' : 'light'];
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ round_number: '', report_link: '', sponsor_id: '', title: '' });
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [filterSponsor, setFilterSponsor] = useState('all');
  const [filterRight, setFilterRight] = useState('all');
  const [exportConfirm, setExportConfirm] = useState(false);
  const [compareA, setCompareA] = useState('');
  const [compareB, setCompareB] = useState('');
  const printRef = useRef(null);
  const isViewer = user?.role === 'sponsor' || user?.role === 'spo_viewer';
  const canViewUsage = hasPerm('view_usage_report');

  const load = async () => {
    try {
      const promises = [api.get('/api/sponsor-reports')];
      if (!isViewer && canViewUsage) {
        promises.push(api.get('/api/reports/usage'));
        promises.push(api.get('/api/sponsors'));
      } else if (!isViewer) {
        promises.push(Promise.resolve({ data: [] }));
        promises.push(api.get('/api/sponsors'));
      }
      const results = await Promise.all(promises);
      setSponsorReports(results[0].data);
      if (!isViewer) {
        setReport(results[1].data);
        setSponsors(results[2].data);
      }
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.round_number || !form.report_link || !form.sponsor_id) { toast.error(t('fill_required')); return; }
    try { new URL(form.report_link); } catch { toast.error('Report link must be a valid URL (e.g. https://...)'); return; }
    setSaving(true);
    try {
      await api.post('/api/sponsor-reports', form);
      toast.success(t('report_created'));
      setCreateOpen(false);
      setForm({ round_number: '', report_link: '', sponsor_id: '', title: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const handleDelete = async (reportId) => {
    try {
      await api.delete(`/api/sponsor-reports/${reportId}`);
      toast.success(t('deleted'));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setConfirmDeleteId(null);
  };

  const exportPDF = async () => {
    if (!printRef.current) return;
    setExporting(true);
    setPrintMode(true);
    // let the light palette paint before the canvas snapshot
    await new Promise(r => setTimeout(r, 120));
    try {
      const canvas = await html2canvas(printRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save('Sponsor_Usage_Report.pdf');
      toast.success(t('pdf_exported') || 'PDF exported');
    } catch { toast.error('Export failed'); }
    setPrintMode(false);
    setExporting(false);
  };

  const chartData = report.map(s => ({ name: s.sponsor_name || 'N/A', usage: s.total_used || 0 }));

  // Get unique rights list from all report data for filtering
  const allRights = [];
  const seenRights = new Set();
  report.forEach(s => (s.rights || []).forEach(r => {
    if (!seenRights.has(r.right_id)) { seenRights.add(r.right_id); allRights.push({ id: r.right_id, name: r.right_name }); }
  }));

  // Filter report data — include all sponsors from list when "all" selected
  const filteredReport = (() => {
    let data = report
      .filter(s => filterSponsor === 'all' || s.sponsor_id === filterSponsor)
      .map(s => ({
        ...s,
        rights: (s.rights || []).filter(r => filterRight === 'all' || r.right_id === filterRight),
      }));
    // When specific right is selected, filter out sponsors with 0 matching rights
    if (filterRight !== 'all') {
      data = data.filter(s => s.rights.length > 0);
    }
    // When "All Sponsors" is selected, add sponsors that have 0 assignments
    if (filterSponsor === 'all') {
      const reportedIds = new Set(data.map(s => s.sponsor_id));
      const missedSponsors = sponsors
        .filter(s => !reportedIds.has(s.id) && s.status === 'active')
        .map(s => ({ sponsor_id: s.id, sponsor_name: s.company_name, logo_url: s.logo_url || '', rights: [], total_used: 0 }));
      data = [...data, ...missedSponsors];
    }
    return data;
  })();

  const filteredChartData = filteredReport.map(s => ({ name: s.sponsor_name || 'N/A', usage: s.rights.reduce((sum, r) => sum + (r.used_amount || 0), 0) }));

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400 text-sm">{t('loading')}</div>;

  // Sponsor/SPO Viewer: only see their reports
  if (isViewer) {
    return (
      <div data-testid="reports-page" className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('reports')}</h1>
          <p className="text-gray-400 mt-1 text-sm">{t('sponsor_reports_desc')}</p>
        </div>
        {sponsorReports.length === 0 ? (
          <Card className="bg-white border-gray-200/80 shadow-none">
            <CardContent className="py-12 text-center text-gray-400 text-sm">{t('no_reports')}</CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sponsorReports.map(r => (
              <Card key={r.id} className="bg-white border-gray-200/80 shadow-none hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <p className="font-medium text-gray-900 text-sm">{r.title}</p>
                    <Badge variant="outline" className="text-[10px] border-gray-200 text-gray-500">R{r.round_number}</Badge>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    {r.sponsor_logo_url ? (
                      <img src={r.sponsor_logo_url} alt="" className="h-5 w-5 rounded object-cover" />
                    ) : (
                      <div className="h-5 w-5 rounded bg-gray-100 flex items-center justify-center text-[8px] font-bold text-gray-400">{r.sponsor_name?.[0]}</div>
                    )}
                    <p className="text-xs font-medium text-gray-700">{r.sponsor_name}</p>
                  </div>
                  <p className="text-xs text-gray-400 mb-3">{t('created_by')}: {r.created_by_name} | {r.created_at?.split('T')[0]}</p>
                  <a href={r.report_link} target="_blank" rel="noopener noreferrer" data-testid={`report-link-${r.id}`}
                    className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium">
                    <ExternalLink size={13} /> {t('view_report')}
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Admin/Manager: tabs for usage + sponsor reports
  return (
    <div data-testid="reports-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('reports')}</h1>
          <p className="text-gray-400 mt-1 text-sm">{t('reports_desc')}</p>
        </div>
      </div>

      <Tabs defaultValue="sponsor_reports">
        <TabsList className="bg-gray-100/80 border border-gray-200/60">
          <TabsTrigger value="sponsor_reports" className="text-xs">{t('sponsor_reports')}</TabsTrigger>
          {canViewUsage && <TabsTrigger value="usage" className="text-xs">{t('usage_reports')}</TabsTrigger>}
          {canViewUsage && <TabsTrigger value="comparison" className="text-xs">{t('comparison', 'Comparison')}</TabsTrigger>}
        </TabsList>

        <TabsContent value="sponsor_reports" className="space-y-4 mt-4">
          <div className="flex justify-end">
            {!isViewer && hasPerm('create_reports') && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-report-btn" className="bg-gray-900 text-white hover:bg-gray-800 text-sm h-9">
                  <Plus size={15} className="mr-1.5" /> {t('create_report')}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-gray-200 max-w-md">
                <DialogHeader><DialogTitle className="text-lg font-semibold text-gray-900">{t('create_report')}</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div><Label className="text-sm text-gray-600">{t('sponsor')} *</Label>
                    <Select value={form.sponsor_id} onValueChange={v => setForm(p => ({...p, sponsor_id: v}))}>
                      <SelectTrigger data-testid="report-sponsor-select" className="bg-gray-50 border-gray-200 h-10 text-sm mt-1"><SelectValue placeholder={t('select_sponsor')} /></SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {sponsors.map(s => <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                  <div><Label className="text-sm text-gray-600">{t('round_number')} *</Label>
                    <Input data-testid="report-round" value={form.round_number} onChange={e => setForm(p => ({...p, round_number: e.target.value}))} placeholder="e.g. 15" className="bg-gray-50 border-gray-200 h-10 text-sm mt-1" /></div>
                  <div><Label className="text-sm text-gray-600">{t('title')}</Label>
                    <Input data-testid="report-title" value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder={t('report_title_placeholder')} className="bg-gray-50 border-gray-200 h-10 text-sm mt-1" /></div>
                  <div><Label className="text-sm text-gray-600">{t('report_link')} *</Label>
                    <Input data-testid="report-link-input" type="url" value={form.report_link} onChange={e => setForm(p => ({...p, report_link: e.target.value}))} placeholder="https://drive.google.com/..." className="bg-gray-50 border-gray-200 h-10 text-sm mt-1" /></div>
                  <Button data-testid="save-report-btn" onClick={handleCreate} disabled={saving} className="w-full bg-gray-900 text-white hover:bg-gray-800 text-sm h-10">
                    {saving ? '...' : t('create_report')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            )}
          </div>

          {sponsorReports.length === 0 ? (
            <Card className="bg-white border-gray-200/80 shadow-none">
              <CardContent className="py-12 text-center text-gray-400 text-sm">{t('no_reports')}</CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {sponsorReports.map(r => (
                <Card key={r.id} className="bg-white border-gray-200/80 shadow-none hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {r.sponsor_logo_url ? (
                          <img src={r.sponsor_logo_url} alt="" className="h-6 w-6 rounded object-cover border border-gray-100" />
                        ) : (
                          <div className="h-6 w-6 rounded bg-gray-100 flex items-center justify-center text-[9px] font-bold text-gray-400">{r.sponsor_name?.[0]}</div>
                        )}
                        <p className="font-medium text-gray-900 text-sm">{r.title}</p>
                      </div>
                      <Badge variant="outline" className="text-[10px] border-gray-200 text-gray-500">R{r.round_number}</Badge>
                    </div>
                    <p className="text-xs text-gray-400 mb-1">{r.sponsor_name}</p>
                    <p className="text-xs text-gray-400 mb-3">{r.created_at?.split('T')[0]}</p>
                    <div className="flex items-center justify-between">
                      <a href={r.report_link} target="_blank" rel="noopener noreferrer" data-testid={`report-link-${r.id}`}
                        className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium">
                        <ExternalLink size={13} /> {t('view_report')}
                      </a>
                      {!isViewer && hasPerm('create_reports') && (
                      <Button variant="ghost" size="sm" onClick={() => setConfirmDeleteId(r.id)} className="h-7 w-7 p-0 text-gray-400 hover:text-red-500">
                        <Trash2 size={13} />
                      </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="usage" className="space-y-4 mt-4">
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <select data-testid="filter-sponsor" value={filterSponsor} onChange={e => setFilterSponsor(e.target.value)}
                className="text-xs h-8 px-2 rounded-md border border-[var(--control)] bg-[var(--surface)] text-[var(--ink-2)] focus:outline-none focus:ring-1 focus:ring-[var(--control)]">
                <option value="all">All Sponsors</option>
                {sponsors.map(s => <option key={s.id} value={s.id}>{s.company_name}</option>)}
              </select>
              <select data-testid="filter-right" value={filterRight} onChange={e => setFilterRight(e.target.value)}
                className="text-xs h-8 px-2 rounded-md border border-[var(--control)] bg-[var(--surface)] text-[var(--ink-2)] focus:outline-none focus:ring-1 focus:ring-[var(--control)]">
                <option value="all">All Rights</option>
                {allRights.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            {hasPerm('view_usage_report') && (
            <Button data-testid="export-report-pdf" variant="outline" size="sm" onClick={() => setExportConfirm(true)} disabled={exporting} className="text-sm h-8 border-gray-200">
              <Download size={14} className="mr-1.5" /> {exporting ? '...' : `${t('export')} PDF`}
            </Button>
            )}
            <ConfirmDialog open={exportConfirm} onCancel={() => setExportConfirm(false)}
              onConfirm={() => { setExportConfirm(false); exportPDF(); }}
              title="Export Usage Report"
              description={`Export ${filterSponsor === 'all' ? 'all sponsors' : sponsors.find(s => s.id === filterSponsor)?.company_name || ''} ${filterRight === 'all' ? '(all rights)' : `(${allRights.find(r => r.id === filterRight)?.name || ''})`} as PDF.`}
              confirmText="Export PDF" />
          </div>

          {/* PDF-optimized printable area */}
          <div ref={printRef} style={{ padding: '24px', background: P.bg, color: P.ink }}>
            {/* Report Header */}
            <div style={{ borderBottom: '3px solid #111827', paddingBottom: '16px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h1 style={{ fontSize: '22px', fontWeight: '800', color: P.ink, letterSpacing: '-0.5px', margin: 0 }}>INCEPTION Sports Hub</h1>
                  <p style={{ fontSize: '15px', fontWeight: '600', color: P.ink2, marginTop: '4px' }}>Sponsor Rights Usage Report</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: '11px', color: P.muted, margin: 0 }}>Generated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  <p style={{ fontSize: '11px', color: P.muted, margin: '2px 0 0' }}>
                    {filterSponsor === 'all' ? 'All Sponsors' : sponsors.find(s => s.id === filterSponsor)?.company_name || ''} | {filterRight === 'all' ? 'All Rights' : allRights.find(r => r.id === filterRight)?.name || ''}
                  </p>
                </div>
              </div>
            </div>

            {/* Summary Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
              {[
                { label: 'Total Sponsors', value: filteredReport.length },
                { label: 'Total Rights', value: filteredReport.reduce((s, sp) => s + (sp.rights?.length || 0), 0) },
                { label: 'Total Allocated', value: filteredReport.reduce((s, sp) => s + (sp.rights || []).reduce((a, r) => a + (r.allocation_type === 'unlimited' ? 0 : (r.allocated_amount || 0)), 0), 0) },
                { label: 'Total Used', value: filteredReport.reduce((s, sp) => s + (sp.rights || []).reduce((a, r) => a + (r.used_amount || 0), 0), 0) },
              ].map((stat, i) => (
                <div key={i} style={{ background: P.panel, border: '1px solid #E5E7EB', borderRadius: '8px', padding: '12px 16px' }}>
                  <p style={{ fontSize: '10px', fontWeight: '600', color: P.faint, textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>{stat.label}</p>
                  <p style={{ fontSize: '20px', fontWeight: '700', color: P.ink, margin: '4px 0 0' }}>{stat.value}</p>
                </div>
              ))}
            </div>

            {/* Chart */}
            {filteredChartData.length > 0 && (
              <div className="mb-6 rounded-xl p-4" style={{ border: `1px solid ${P.line}` }}>
                <p className="text-xs font-semibold mb-3" style={{ color: P.muted }}>Usage Overview</p>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={filteredChartData} barCategoryGap="25%" margin={{ right: 10 }}>
                    <XAxis dataKey="name" tick={{ fill: P.faint, fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: P.faint, fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ backgroundColor: P.bg, color: P.ink, border: `1px solid ${P.line}`, borderRadius: 12, fontSize: 12 }} />
                    <Bar dataKey="usage" fill={P.chartBar} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Sponsor Tables */}
            {filteredReport.length === 0 ? <p style={{ color: P.faint, fontSize: '13px', textAlign: 'center', padding: '32px 0' }}>{t('no_data')}</p> :
            filteredReport.map((sponsor, idx) => (
              <div key={sponsor.sponsor_id} style={{ marginBottom: '20px', border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden', breakInside: 'avoid' }}>
                {/* Sponsor Header */}
                <div style={{ background: P.panel, padding: '10px 16px', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {sponsor.logo_url ? (
                    <img src={sponsor.logo_url} alt="" style={{ height: '24px', width: '24px', borderRadius: '4px', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ height: '24px', width: '24px', borderRadius: '4px', background: P.lineSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700', color: P.faint }}>{sponsor.sponsor_name?.[0]}</div>
                  )}
                  <span style={{ fontSize: '13px', fontWeight: '700', color: P.ink }}>{sponsor.sponsor_name}</span>
                  <span style={{ fontSize: '10px', color: P.faint, marginLeft: 'auto' }}>{(sponsor.rights || []).length} rights</span>
                </div>

                {/* Rights Table */}
                {(sponsor.rights || []).length === 0 ? (
                  <p style={{ color: P.faint, fontSize: '11px', padding: '12px 16px', fontStyle: 'italic', margin: 0 }}>No rights assigned</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                        <th style={{ textAlign: 'left', padding: '8px 16px', fontSize: '10px', fontWeight: '600', color: P.faint, textTransform: 'uppercase' }}>Right</th>
                        <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '10px', fontWeight: '600', color: P.faint, textTransform: 'uppercase' }}>Type</th>
                        <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '10px', fontWeight: '600', color: P.faint, textTransform: 'uppercase' }}>Allocated</th>
                        <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '10px', fontWeight: '600', color: P.faint, textTransform: 'uppercase' }}>Used</th>
                        <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '10px', fontWeight: '600', color: P.faint, textTransform: 'uppercase' }}>Remaining</th>
                        <th style={{ textAlign: 'right', padding: '8px 16px', fontSize: '10px', fontWeight: '600', color: P.faint, textTransform: 'uppercase', width: '120px' }}>Usage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(sponsor.rights || []).map((r, rIdx) => {
                        const pct = r.allocation_type === 'unlimited' ? 0 : r.allocated_amount ? Math.round((r.used_amount / r.allocated_amount) * 100) : 0;
                        const barColor = pct > 80 ? '#EF4444' : pct > 50 ? '#F59E0B' : P.bar;
                        return (
                          <tr key={r.right_id} style={{ borderBottom: rIdx < (sponsor.rights || []).length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                            <td style={{ padding: '8px 16px', fontWeight: '500', color: P.ink }}>
                              {r.right_name}
                              {r.right_status && r.right_status !== 'active' && (
                                <span style={{ fontSize: '9px', marginLeft: '6px', padding: '1px 6px', borderRadius: '4px', background: P.tagBg, color: P.tagFg, textTransform: 'capitalize', fontWeight: 600 }}>{r.right_status}</span>
                              )}
                            </td>
                            <td style={{ padding: '8px 12px', color: P.muted }}>
                              <span style={{ fontSize: '10px', padding: '2px 8px', background: P.lineSoft, borderRadius: '4px', textTransform: 'capitalize' }}>{r.unit_type}</span>
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', color: P.ink2 }}>{r.allocation_type === 'unlimited' ? '∞' : r.allocated_amount}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', color: P.ink2, fontWeight: '600' }}>{r.used_amount || 0}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', color: P.ink2 }}>{r.allocation_type === 'unlimited' ? '∞' : r.remaining_amount}</td>
                            <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                              {r.allocation_type === 'unlimited' ? (
                                <span style={{ fontSize: '10px', color: '#10B981', fontWeight: '600' }}>Unlimited</span>
                              ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
                                  <div style={{ width: '60px', height: '6px', background: P.lineSoft, borderRadius: '3px', overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: barColor, borderRadius: '3px' }} />
                                  </div>
                                  <span style={{ fontSize: '10px', color: P.muted, minWidth: '28px' }}>{pct}%</span>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            ))}

            {/* Footer */}
            <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '12px', marginTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
              <p style={{ fontSize: '9px', color: P.faint, margin: 0 }}>INCEPTION Sports Hub — Confidential</p>
              <p style={{ fontSize: '9px', color: P.faint, margin: 0 }}>Page 1</p>
            </div>
          </div>
        </TabsContent>

        {canViewUsage && (
        <TabsContent value="comparison" className="space-y-4 mt-4">
 <Card className="bg-white dark:bg-[var(--surface)] border-0 shadow-card rounded-card">
            <CardHeader className="pb-3 pt-5 px-5">
              <CardTitle className="section-title flex items-center gap-2">
                <GitCompare size={18} className="text-indigo-500" /> {t('sponsor_comparison', 'Sponsor Comparison')}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-5">
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="flex-1">
                  <label className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-1 block">{t('sponsor', 'Sponsor')} A</label>
                  <Select value={compareA} onValueChange={setCompareA}>
                    <SelectTrigger data-testid="compare-sponsor-a" className="bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 h-9 text-sm">
                      <SelectValue placeholder={t('select_sponsor', 'Select sponsor')} />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
                      {sponsors.filter(s => s.id !== compareB).map(s => <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end pb-1 text-gray-300 dark:text-gray-600 font-bold text-sm">vs</div>
                <div className="flex-1">
                  <label className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-1 block">{t('sponsor', 'Sponsor')} B</label>
                  <Select value={compareB} onValueChange={setCompareB}>
                    <SelectTrigger data-testid="compare-sponsor-b" className="bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 h-9 text-sm">
                      <SelectValue placeholder={t('select_sponsor', 'Select sponsor')} />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
                      {sponsors.filter(s => s.id !== compareA).map(s => <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {compareA && compareB ? (() => {
                const dataA = report.find(r => r.sponsor_id === compareA);
                const dataB = report.find(r => r.sponsor_id === compareB);
                const nameA = sponsors.find(s => s.id === compareA)?.company_name || 'Sponsor A';
                const nameB = sponsors.find(s => s.id === compareB)?.company_name || 'Sponsor B';

                // Merge rights from both sponsors for comparison chart
                const rightsMap = {};
                (dataA?.rights || []).forEach(r => {
                  rightsMap[r.right_name] = { name: r.right_name.length > 15 ? r.right_name.substring(0, 15) + '…' : r.right_name, a: r.used_amount || 0, b: 0 };
                });
                (dataB?.rights || []).forEach(r => {
                  const key = r.right_name;
                  if (rightsMap[key]) { rightsMap[key].b = r.used_amount || 0; }
                  else { rightsMap[key] = { name: key.length > 15 ? key.substring(0, 15) + '…' : key, a: 0, b: r.used_amount || 0 }; }
                });
                const compareChartData = Object.values(rightsMap);
                const totalA = (dataA?.rights || []).reduce((s, r) => s + (r.used_amount || 0), 0);
                const totalB = (dataB?.rights || []).reduce((s, r) => s + (r.used_amount || 0), 0);

                return (
                  <div className="space-y-5">
                    {/* Summary cards */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="rounded-xl bg-blue-50 dark:bg-blue-950/30 p-4">
                        <p className="text-xs text-blue-500 dark:text-blue-400 font-medium mb-1">{nameA}</p>
                        <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{totalA}</p>
                        <p className="text-[10px] text-blue-400 dark:text-blue-500">{(dataA?.rights || []).length} {t('rights', 'rights')}</p>
                      </div>
                      <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/30 p-4">
                        <p className="text-xs text-emerald-500 dark:text-emerald-400 font-medium mb-1">{nameB}</p>
                        <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{totalB}</p>
                        <p className="text-[10px] text-emerald-400 dark:text-emerald-500">{(dataB?.rights || []).length} {t('rights', 'rights')}</p>
                      </div>
                    </div>

                    {/* Grouped bar chart */}
                    {compareChartData.length > 0 && (
                      <div className="rounded-xl border border-gray-100 dark:border-gray-800 p-4">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">{t('usage_by_right', 'Usage by Right')}</p>
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={compareChartData} barCategoryGap="25%" margin={{ right: 10 }}>
                            <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 10 }} axisLine={false} tickLine={false} />
                            <YAxis hide />
                            <Tooltip contentStyle={{ backgroundColor: '#fff', border: 'none', borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 12 }} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="a" name={nameA} fill="#3B82F6" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="b" name={nameB} fill="#10B981" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Side-by-side rights table */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {[{ data: dataA, name: nameA, color: 'blue' }, { data: dataB, name: nameB, color: 'emerald' }].map(({ data: d, name, color }) => {
                        const bgMap = { blue: 'bg-blue-50 dark:bg-blue-950/30', emerald: 'bg-emerald-50 dark:bg-emerald-950/30' };
                        const textMap = { blue: 'text-blue-700 dark:text-blue-300', emerald: 'text-emerald-700 dark:text-emerald-300' };
                        return (
                        <div key={name} className="rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
                          <div className={`px-4 py-2.5 ${bgMap[color]} border-b border-gray-100 dark:border-gray-800`}>
                            <p className={`text-xs font-semibold ${textMap[color]}`}>{name}</p>
                          </div>
                          <div className="divide-y divide-gray-50 dark:divide-gray-800/50">
                            {(d?.rights || []).length === 0 ? (
                              <p className="text-xs text-gray-400 py-4 text-center italic">{t('no_rights_assigned', 'No rights assigned')}</p>
                            ) : (d?.rights || []).map(r => {
                              const pct = r.allocation_type === 'unlimited' ? 0 : r.allocated_amount ? Math.round((r.used_amount / r.allocated_amount) * 100) : 0;
                              return (
                                <div key={r.right_id} className="px-4 py-2.5 flex items-center justify-between gap-2">
                                  <div className="min-w-0">
                                    <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
                                      {r.right_name}
                                      {r.right_status && r.right_status !== 'active' && (
                                        <span className="ms-1.5 text-[9px] font-semibold capitalize text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40 rounded px-1.5 py-0.5">{r.right_status}</span>
                                      )}
                                    </p>
                                    <p className="text-[10px] text-gray-400">{r.allocation_type === 'unlimited' ? '∞' : r.allocated_amount} {t('allocated', 'alloc.')}</p>
                                  </div>
                                  <div className="text-right shrink-0">
                                    <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{r.used_amount || 0}</p>
                                    <p className="text-[10px] text-gray-400">{r.allocation_type === 'unlimited' ? '∞' : `${pct}%`}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                      })}
                    </div>
                  </div>
                );
              })() : (
                <div className="py-12 text-center text-gray-400">
                  <GitCompare size={40} className="mx-auto mb-3 text-gray-200 dark:text-gray-700" />
                  <p className="text-sm">{t('select_two_sponsors', 'Select two sponsors to compare their rights usage')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>
      <ConfirmDialog open={!!confirmDeleteId} onCancel={() => setConfirmDeleteId(null)}
        onConfirm={() => handleDelete(confirmDeleteId)}
        title="Delete Report" description="Are you sure you want to delete this report?" confirmText="Delete" destructive />
    </div>
  );
}
